import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../../services/api';
import TrendsComponent from './TrendsComponent';
import type { InstagramPost, BrandAsset, ContentTheme, MediaJob } from './types';
import { CONTENT_THEMES, STATUS_STYLES, formatStatus } from './types';

// The studio is a linear pipeline: mine a trend, compose from it, approve,
// publish. History is reference material rather than a stage, so it collapses
// out of the flow instead of sitting alongside it as a peer.
type StudioStep = 'trends' | 'compose' | 'approve' | 'publish';

const STEPS: { key: StudioStep; label: string }[] = [
  { key: 'trends', label: 'Trends' },
  { key: 'compose', label: 'Compose' },
  { key: 'approve', label: 'Approve' },
  { key: 'publish', label: 'Publish' },
];

/**
 * The clock the business schedules in. Mirrors BRAND_TIMEZONE on the server,
 * which is what actually interprets the time — this is only for labelling, so
 * an admin is never left guessing whose 9am they just picked.
 */
const BRAND_TZ_LABEL = 'ET';

/**
 * `datetime-local` needs `YYYY-MM-DDTHH:mm`, and it is the earliest time worth
 * offering. Built in the brand zone rather than from the raw clock, or an
 * admin in another timezone would be blocked from picking hours that are
 * perfectly valid in Eastern time.
 */
function nowInBrandZone(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date()).reduce<Record<string, string>>((acc, part) => {
    if (part.type !== 'literal') acc[part.type] = part.value;
    return acc;
  }, {});

  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

interface MediaProvider {
  id: string;
  label: string;
  description: string;
  capabilities: string[];
  configured: boolean;
  unavailableReason: string | null;
}

const ContentStudioComponent: React.FC = () => {
  // Per-post wall-clock values from the date pickers, keyed by post id, so
  // two rows open at once cannot overwrite each other's time.
  const [scheduleDrafts, setScheduleDrafts] = useState<Record<string, string>>({});

  const [studioTab, setStudioTab] = useState<StudioStep>('trends');
  const [showHistory, setShowHistory] = useState(false);

  // Composer state
  const [theme, setTheme] = useState<ContentTheme>('product-showcase');
  const [context, setContext] = useState('');
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<BrandAsset | null>(null);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);

  // Data
  const [assets, setAssets] = useState<BrandAsset[]>([]);
  const [pendingPosts, setPendingPosts] = useState<InstagramPost[]>([]);
  const [allPosts, setAllPosts] = useState<InstagramPost[]>([]);

  // AI media generation
  const [mediaPrompt, setMediaPrompt] = useState('');
  // Optional real product image to seed generation from.
  const [baseAsset, setBaseAsset] = useState<BrandAsset | null>(null);
  const [generatedMedia, setGeneratedMedia] = useState<BrandAsset[]>([]);
  const [mediaBusy, setMediaBusy] = useState<null | 'image' | 'video' | 'recreate'>(null);
  const [videoProgress, setVideoProgress] = useState('');

  // Which backend renders. Gemini builds a scene from the brief; Higgsfield
  // models a new reel on one that already works. They are different jobs, so
  // this is a deliberate choice rather than a fallback.
  const [providers, setProviders] = useState<MediaProvider[]>([]);
  const [provider, setProvider] = useState('gemini');
  const [product, setProduct] = useState<{ title: string; imageUrl: string } | null>(null);

  // Recreate: the reel being modelled, before and after it is stored.
  const [inspirationFile, setInspirationFile] = useState<File | null>(null);

  // UI state
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [contentEnabled, setContentEnabled] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Which post has an action in flight, and which action. Every button on
  // that post is disabled until it settles — publishing in particular is slow
  // enough (Instagram transcodes video before accepting it) that an
  // unguarded button invites a double post.
  const [busyPost, setBusyPost] = useState<{ id: string; action: string } | null>(null);

  const isBusy = (postId: string) => busyPost?.id === postId;
  const busyAction = (postId: string) => (busyPost?.id === postId ? busyPost.action : null);

  // Stops the video poll loop from calling setState after unmount.
  const pollCancelled = useRef(false);
  useEffect(() => () => { pollCancelled.current = true; }, []);

  // Cleared approval, not yet live. Derived rather than fetched — allPosts
  // already holds them and a second request would only drift.
  // Split rather than lumped together. A scheduled post is not "ready to
  // publish" — it is already spoken for, and showing it under that heading
  // with a Publish Now button invites someone to send it early by accident.
  const approvedPosts = allPosts.filter((p) => p.status === 'approved');

  const scheduledPosts = allPosts
    .filter((p) => p.status === 'scheduled' || p.status === 'publishing')
    .sort((a, b) => (a.scheduledFor || '').localeCompare(b.scheduledFor || ''));

  // Everything still awaiting a decision. Drafts are included because they are
  // work in progress that would otherwise only be reachable through History —
  // easy to write something and then lose track of it.
  const draftPosts = allPosts.filter((p) => p.status === 'draft');

  const notify = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const loadAssets = useCallback(async () => {
    try {
      const res = await apiService.getBrandAssets() as unknown as { assets: BrandAsset[] };
      setAssets(res.assets || []);
      if (res.assets?.length && !selectedAsset) {
        setSelectedAsset(res.assets[0]);
      }
    } catch {
      // Asset list is not critical to the composer working.
    }
    // selectedAsset intentionally omitted: this should only seed the default once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Turn a completed media job into something the asset grid can render.
  const jobToAsset = (job: MediaJob): BrandAsset => ({
    file: job._id,
    description: job.prompt,
    url: job.mediaUrl || '',
    kind: job.kind,
  });

  const loadGeneratedMedia = useCallback(async () => {
    try {
      const res = await apiService.getMediaJobs({ status: 'completed', limit: 24 }) as unknown as { jobs: MediaJob[] };
      setGeneratedMedia((res.jobs || []).filter((j) => j.mediaUrl).map(jobToAsset));
    } catch {
      // Generated media is additive; the composer still works without it.
    }
  }, []);

  const handleGenerateImage = async () => {
    if (!mediaPrompt.trim()) return;
    setMediaBusy('image');
    try {
      const res = await apiService.generateImage(
        mediaPrompt, undefined, baseAsset?.url, provider,
      ) as unknown as { job: MediaJob };
      const asset = jobToAsset(res.job);
      setGeneratedMedia((prev) => [asset, ...prev]);
      setSelectedAsset(asset);
      notify('success', 'Image generated.');
    } catch (error) {
      notify('error', error instanceof Error ? error.message : 'Image generation failed.');
    } finally {
      setMediaBusy(null);
    }
  };

  /**
   * Watch a started video job until it settles.
   *
   * Shared by plain generation and recreate: both return the same job row and
   * both are slow enough that the request cannot hold the result, so the only
   * difference between them is which route started the job.
   */
  const pollVideoJob = async (jobId: string, label = 'Video') => {
    const startedAt = Date.now();

    // Give up after 10 minutes; Veo's documented ceiling is ~6, and a
    // Higgsfield render that has not landed by then has gone wrong.
    while (Date.now() - startedAt < 10 * 60 * 1000) {
      if (pollCancelled.current) return;

      await new Promise((resolve) => setTimeout(resolve, 10000));
      if (pollCancelled.current) return;

      const elapsed = Math.round((Date.now() - startedAt) / 1000);
      setVideoProgress(`Rendering… ${elapsed}s elapsed`);

      const poll = await apiService.getMediaJob(jobId) as unknown as { job: MediaJob };

      if (poll.job.status === 'completed') {
        const asset = jobToAsset(poll.job);
        setGeneratedMedia((prev) => [asset, ...prev]);
        setSelectedAsset(asset);
        notify('success', `${label} generated.`);
        return;
      }

      if (poll.job.status === 'failed') {
        notify('error', poll.job.errorMessage || `${label} generation failed.`);
        return;
      }
    }

    notify('error', `${label} is taking unusually long. Check Generated media shortly.`);
  };

  // Veo runs for anywhere from ~11 seconds to several minutes, so the request
  // only starts the job and we poll the job row until it settles.
  const handleGenerateVideo = async () => {
    if (!mediaPrompt.trim()) return;
    setMediaBusy('video');
    setVideoProgress('Starting render…');

    try {
      const res = await apiService.generateVideo(
        mediaPrompt, undefined, undefined, baseAsset?.url, provider,
      ) as unknown as { job: MediaJob };

      await pollVideoJob(res.job._id);
    } catch (error) {
      notify('error', error instanceof Error ? error.message : 'Video generation failed.');
    } finally {
      setMediaBusy(null);
      setVideoProgress('');
    }
  };

  /**
   * Recreate a reel: store the inspiration clip, then render ours from it.
   *
   * The upload is a separate step because the clip has to have a stable URL
   * before Higgsfield can be pointed at it, and because a failure to store it
   * should be reported as an upload problem rather than a generation one.
   */
  const handleRecreate = async () => {
    if (!inspirationFile) return;

    setMediaBusy('recreate');
    setVideoProgress('Uploading the reference reel…');

    try {
      const uploaded = await apiService.uploadInspiration(inspirationFile) as unknown as {
        upload: { url: string };
      };

      if (pollCancelled.current) return;
      setVideoProgress('Starting render…');

      const res = await apiService.recreateReel(
        uploaded.upload.url, mediaPrompt, '9:16',
      ) as unknown as { job: MediaJob };

      await pollVideoJob(res.job._id, 'Reel');
    } catch (error) {
      notify('error', error instanceof Error ? error.message : 'Recreate failed.');
    } finally {
      setMediaBusy(null);
      setVideoProgress('');
    }
  };

  const loadPosts = useCallback(async () => {
    try {
      const [pendingRes, allRes] = await Promise.all([
        apiService.getInstagramPosts({ status: 'pending_approval' }),
        apiService.getInstagramPosts({ limit: 50 }),
      ]);
      setPendingPosts((pendingRes as unknown as { posts: InstagramPost[] }).posts || []);
      setAllPosts((allRes as unknown as { posts: InstagramPost[] }).posts || []);
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Failed to load posts');
    }
  }, []);

  const loadConnection = useCallback(async () => {
    try {
      const res = await apiService.getInstagramConnectionStatus() as unknown as {
        connection: { contentGenerationEnabled: boolean };
      };
      setContentEnabled(res.connection.contentGenerationEnabled);
    } catch {
      // Assume enabled; the generate call will surface the real error.
    }
  }, []);

  const loadProviders = useCallback(async () => {
    try {
      const res = await apiService.getMediaProviders() as unknown as {
        providers: MediaProvider[];
        defaultProvider: string;
        product: { title: string; imageUrl: string };
      };

      setProviders(res.providers || []);
      setProduct(res.product || null);

      // Land on something that can actually run. Defaulting to a provider
      // that is switched off would put the failure at generation time,
      // after the admin has written the brief.
      const usable = (res.providers || []).find((candidate) => candidate.configured);
      setProvider(usable ? usable.id : res.defaultProvider);
    } catch {
      // The picker is additive; Gemini stays selected and working without it.
    }
  }, []);

  useEffect(() => {
    loadAssets();
    loadPosts();
    loadConnection();
    loadGeneratedMedia();
    loadProviders();
  }, [loadAssets, loadPosts, loadConnection, loadGeneratedMedia, loadProviders]);

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const res = await apiService.generateCaption(theme, context) as unknown as {
        caption: string; hashtags: string[]; suggestedMedia: string;
      };

      setCaption(res.caption);
      setHashtags(res.hashtags || []);

      const suggested = assets.find((a) => a.file === res.suggestedMedia);
      if (suggested) setSelectedAsset(suggested);

      notify('success', 'Caption generated');
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSuggestHashtags = async () => {
    if (!caption.trim()) {
      notify('error', 'Write a caption first');
      return;
    }

    setIsGenerating(true);
    try {
      const res = await apiService.suggestHashtags(caption, theme) as unknown as { hashtags: string[] };
      setHashtags(res.hashtags || []);
      notify('success', 'Hashtags suggested');
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Suggestion failed');
    } finally {
      setIsGenerating(false);
    }
  };

  const resetComposer = () => {
    setCaption('');
    setHashtags([]);
    setContext('');
    setEditingPostId(null);
  };

  const handleSaveDraft = async () => {
    if (!caption.trim()) {
      notify('error', 'A caption is required');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        caption,
        hashtags,
        contentTheme: theme,
        brandAsset: selectedAsset?.file,
        mediaUrls: selectedAsset ? [selectedAsset.url] : [],
        mediaType: selectedAsset?.kind === 'video' ? 'VIDEO' : 'IMAGE',
        generatedBy: 'ai',
      };

      if (editingPostId) {
        await apiService.updateInstagramPost(editingPostId, payload);
        notify('success', 'Draft updated');
      } else {
        await apiService.createInstagramPost(payload);
        notify('success', 'Draft saved');
      }

      resetComposer();
      loadPosts();
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Save failed');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndSubmit = async () => {
    if (!caption.trim()) {
      notify('error', 'A caption is required');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        caption,
        hashtags,
        contentTheme: theme,
        brandAsset: selectedAsset?.file,
        mediaUrls: selectedAsset ? [selectedAsset.url] : [],
        mediaType: selectedAsset?.kind === 'video' ? 'VIDEO' : 'IMAGE',
        generatedBy: 'ai',
      };

      let postId = editingPostId;
      if (postId) {
        await apiService.updateInstagramPost(postId, payload);
      } else {
        const created = await apiService.createInstagramPost(payload) as unknown as {
          post: InstagramPost;
        };
        postId = created.post._id;
      }

      await apiService.submitPostForApproval(postId!);
      notify('success', 'Submitted for approval');
      resetComposer();
      loadPosts();
      setStudioTab('approvals');
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Submit failed');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Run a post action with the row locked for its duration.
   *
   * Refuses to start when something is already in flight for that post: these
   * calls mutate server state, and a second click during the round trip can
   * approve twice or, worse, publish twice.
   */
  const runPostAction = async (
    postId: string,
    action: string,
    fn: () => Promise<unknown>,
    successMessage: string,
    failureMessage: string,
  ) => {
    if (busyPost) return;

    setBusyPost({ id: postId, action });
    try {
      await fn();
      notify('success', successMessage);
      // Reload before clearing the lock, so the row cannot be re-actioned in
      // the gap between the call returning and the list catching up.
      await loadPosts();
    } catch (err) {
      notify('error', err instanceof Error ? err.message : failureMessage);
    } finally {
      setBusyPost(null);
    }
  };

  const handleSubmitForApproval = (postId: string) => runPostAction(
    postId, 'submit',
    () => apiService.submitPostForApproval(postId),
    'Sent for approval', 'Could not submit',
  );

  const handleApprove = (postId: string) => runPostAction(
    postId, 'approve',
    () => apiService.approveInstagramPost(postId),
    'Post approved', 'Approve failed',
  );

  const handleReject = (postId: string) => runPostAction(
    postId, 'reject',
    () => apiService.rejectInstagramPost(postId, 'Sent back for edits'),
    'Post sent back to drafts', 'Reject failed',
  );

  const handlePublish = (postId: string) => runPostAction(
    postId, 'publish',
    () => apiService.publishInstagramPost(postId),
    'Published to Instagram', 'Publish failed',
  );

  /**
   * Scheduling a post for later.
   *
   * The picker holds a bare wall-clock value — exactly what <input
   * type="datetime-local"> produces — and it is sent as typed. The server
   * reads it as brand time, so the hour the admin picked is the hour the post
   * goes out regardless of where they happen to be sitting. Converting here
   * would mean two implementations of the same timezone rule, and the one in
   * the browser would silently use the laptop's zone.
   */
  const handleSchedule = (postId: string) => {
    const when = scheduleDrafts[postId];
    if (!when) {
      notify('error', 'Pick a date and time first.');
      return;
    }

    return runPostAction(
      postId, 'schedule',
      () => apiService.scheduleInstagramPost(postId, when),
      `Scheduled for ${when.replace('T', ' ')} ${BRAND_TZ_LABEL}`,
      'Could not schedule',
    );
  };

  const handleUnschedule = (postId: string) => runPostAction(
    postId, 'unschedule',
    () => apiService.unscheduleInstagramPost(postId),
    'Taken out of the queue', 'Could not unschedule',
  );

  const handleEdit = (post: InstagramPost) => {
    setCaption(post.caption);
    setHashtags(post.hashtags);
    setEditingPostId(post._id);
    if (post.contentTheme) setTheme(post.contentTheme);
    const asset = assets.find((a) => a.file === post.brandAsset);
    if (asset) setSelectedAsset(asset);
    setStudioTab('compose');
  };

  const handleDelete = (postId: string) => runPostAction(
    postId, 'delete',
    () => apiService.deleteInstagramPost(postId),
    'Post deleted', 'Delete failed',
  );

  const previewCaption = hashtags.length
    ? `${caption}\n\n${hashtags.map((t) => `#${t}`).join(' ')}`
    : caption;

  return (
    <div className="space-y-6">
      {message && (
        <div className={`rounded-xl px-6 py-4 font-body ${
          message.type === 'success'
            ? 'bg-scarf-green/10 border border-scarf-green/30 text-scarf-green'
            : 'bg-brand-red/10 border border-brand-red/30 text-brand-red'
        }`}>
          {message.text}
        </div>
      )}

      {!contentEnabled && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-xl px-6 py-4">
          <p className="font-body text-yellow-800">
            AI generation is unavailable — <code className="font-mono text-sm">GEMINI_API_KEY</code> is not
            set on the server. You can still write and manage posts manually.
          </p>
        </div>
      )}

      {/* Pipeline. Steps stay clickable rather than locked: a post already in
          the queue should not force you back through composing to reach it. */}
      <div className="bg-white rounded-2xl p-4 border border-ice-teal-light shadow-sm">
        <div className="flex items-center">
          {STEPS.map((step, index) => {
            const isActive = studioTab === step.key;
            const isPast = STEPS.findIndex((s) => s.key === studioTab) > index;
            const count = step.key === 'approve'
              ? pendingPosts.length
              : step.key === 'publish'
                ? approvedPosts.length
                : 0;

            return (
              <React.Fragment key={step.key}>
                <button
                  onClick={() => setStudioTab(step.key)}
                  className="flex items-center gap-3 px-3 py-2 rounded-xl transition-colors hover:bg-frost-white"
                >
                  <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                    isActive
                      ? 'bg-brand-blue text-white'
                      : isPast
                        ? 'bg-ice-teal text-deep-navy'
                        : 'bg-frost-white text-gray-500 border border-ice-teal-light'
                  }`}>
                    {index + 1}
                  </span>
                  <span className={`font-bold whitespace-nowrap ${isActive ? 'text-deep-navy' : 'text-gray-500'}`}>
                    {step.label}
                    {count > 0 && (
                      <span className="ml-1.5 text-xs bg-brand-red text-white px-1.5 py-0.5 rounded-full align-middle">
                        {count}
                      </span>
                    )}
                  </span>
                </button>

                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-1 ${isPast ? 'bg-ice-teal' : 'bg-ice-teal-light'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Compose */}
      {studioTab === 'compose' && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm space-y-4">
            <h3 className="text-xl font-title font-bold text-deep-navy">
              {editingPostId ? 'Edit Post' : 'New Post'}
            </h3>

            <div>
              <label htmlFor="ig-theme" className="block text-sm font-bold text-deep-navy mb-2 font-body">
                Content Theme
              </label>
              <select
                id="ig-theme"
                value={theme}
                onChange={(e) => setTheme(e.target.value as ContentTheme)}
                className="w-full px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy focus:border-brand-blue outline-none font-body"
              >
                {CONTENT_THEMES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="ig-context" className="block text-sm font-bold text-deep-navy mb-2 font-body">
                Extra Direction <span className="font-normal text-gray-500">(optional)</span>
              </label>
              <input
                id="ig-context"
                type="text"
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="e.g. tie it to the long weekend"
                className="w-full px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy placeholder-gray-400 focus:border-brand-blue outline-none font-body"
              />
            </div>

            <button
              onClick={handleGenerate}
              disabled={isGenerating || !contentEnabled}
              className="w-full bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-6 py-3 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? 'Generating…' : 'Generate with AI'}
            </button>

            <div>
              <label htmlFor="ig-caption" className="block text-sm font-bold text-deep-navy mb-2 font-body">
                Caption
              </label>
              <textarea
                id="ig-caption"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={5}
                placeholder="Write your caption, or generate one above"
                className="w-full px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy placeholder-gray-400 focus:border-brand-blue outline-none font-body resize-none"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label htmlFor="ig-hashtags" className="block text-sm font-bold text-deep-navy font-body">
                  Hashtags
                </label>
                <button
                  onClick={handleSuggestHashtags}
                  disabled={isGenerating || !contentEnabled}
                  className="text-sm text-brand-blue hover:text-brand-blue-dark font-bold disabled:opacity-50"
                >
                  Suggest
                </button>
              </div>
              <input
                id="ig-hashtags"
                type="text"
                value={hashtags.join(', ')}
                onChange={(e) => setHashtags(
                  e.target.value.split(',').map((t) => t.trim().replace(/^#/, '')).filter(Boolean),
                )}
                placeholder="comma, separated, tags"
                className="w-full px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy placeholder-gray-400 focus:border-brand-blue outline-none font-body"
              />
            </div>

            <div>
              <span className="block text-sm font-bold text-deep-navy mb-2 font-body">
                Generate media with AI
              </span>
              {/* Which backend renders. Not a fallback: Gemini builds a scene
                  from the brief, Higgsfield models a reel on one that already
                  works, and they are chosen for different jobs. */}
              {providers.length > 1 && (
                <div className="flex gap-2 mb-3">
                  {providers.map((candidate) => (
                    <button
                      key={candidate.id}
                      onClick={() => candidate.configured && setProvider(candidate.id)}
                      disabled={!candidate.configured}
                      title={candidate.unavailableReason || candidate.description}
                      className={`flex-1 px-3 py-2 rounded-xl border-2 text-sm font-bold font-body transition-all ${
                        provider === candidate.id
                          ? 'border-brand-blue bg-brand-blue text-white'
                          : candidate.configured
                            ? 'border-ice-teal-light bg-frost-white text-gray-600 hover:border-ice-teal'
                            : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {candidate.label}
                      {!candidate.configured && ' — off'}
                    </button>
                  ))}
                </div>
              )}
              {providers.find((c) => c.id === provider)?.unavailableReason && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 font-body">
                  {providers.find((c) => c.id === provider)?.unavailableReason}
                </p>
              )}
              <textarea
                value={mediaPrompt}
                onChange={(e) => setMediaPrompt(e.target.value)}
                rows={2}
                placeholder={provider === 'higgsfield'
                  ? 'What to change from the reference — e.g. morning light, marble bar, slower final beat'
                  : 'Describe the shot — e.g. on a marble bar with condensation, morning light'}
                className="w-full px-4 py-3 rounded-xl border-2 border-ice-teal-light bg-frost-white text-deep-navy placeholder-gray-400 focus:border-brand-blue outline-none font-body resize-none"
              />

              {/* Prompt-driven generation. Higgsfield works from a
                  reference reel instead, so these controls belong to the
                  Gemini path only. */}
              {provider !== 'higgsfield' && (
                <>
              {/* Base asset. With one selected the model composes a scene
                  around the real product instead of inventing a bottle, so
                  the label and shape survive. */}
              <div className="mt-3">
                <span className="block text-xs font-bold text-gray-600 mb-2 font-body">
                  Build on a brand asset (optional)
                </span>
                <div className="flex gap-2 flex-wrap items-center">
                  <button
                    onClick={() => setBaseAsset(null)}
                    className={`px-3 py-2 rounded-lg text-sm font-bold border transition-colors ${
                      baseAsset === null
                        ? 'border-brand-blue bg-brand-blue text-white'
                        : 'border-ice-teal-light bg-frost-white text-gray-600 hover:border-ice-teal'
                    }`}
                  >
                    From scratch
                  </button>
                  {assets.filter((a) => !a.kind).map((asset) => (
                    <button
                      key={asset.file}
                      onClick={() => setBaseAsset(asset)}
                      title={asset.description}
                      className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                        baseAsset?.file === asset.file
                          ? 'border-brand-blue ring-2 ring-brand-blue/30'
                          : 'border-ice-teal-light hover:border-ice-teal'
                      }`}
                    >
                      <img src={asset.url} alt={asset.description} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
                {baseAsset && (
                  <p className="text-xs text-gray-600 font-body mt-2">
                    Keeping <span className="font-bold">{baseAsset.description}</span> intact and building the scene around it.
                  </p>
                )}
              </div>
              <div className="flex gap-3 mt-2">
                <button
                  onClick={handleGenerateImage}
                  disabled={mediaBusy !== null || !mediaPrompt.trim()}
                  className="flex-1 bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-4 py-2.5 rounded-xl transition-colors disabled:opacity-50"
                >
                  {mediaBusy === 'image' ? 'Generating…' : 'Generate Image'}
                </button>
                <button
                  onClick={handleGenerateVideo}
                  disabled={mediaBusy !== null || !mediaPrompt.trim()}
                  className="flex-1 bg-brand-blue-light hover:bg-brand-blue text-white font-bold px-4 py-2.5 rounded-xl transition-colors disabled:opacity-50"
                >
                  {mediaBusy === 'video' ? 'Rendering…' : 'Generate Video'}
                </button>
              </div>
                </>
              )}

              {/* Recreate. The product is attached for you: an admin
                  modelling a reel should not have to find the bottle each
                  time, and should not be able to attach the wrong thing. */}
              {provider === 'higgsfield' && (
                <>
                  {product && (
                    <div className="mt-3 flex items-center gap-3 p-3 rounded-xl border-2 border-ice-teal-light bg-frost-white">
                      <img
                        src={product.imageUrl}
                        alt={product.title}
                        className="w-12 h-12 rounded-lg object-cover border border-ice-teal-light"
                      />
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-deep-navy font-body truncate">
                          {product.title}
                        </p>
                        <p className="text-xs text-gray-600 font-body">
                          Attached to every recreate — the bottle stays as it really is.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="mt-3">
                    <span className="block text-xs font-bold text-gray-600 mb-2 font-body">
                      Reel to model it on
                    </span>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) => setInspirationFile(e.target.files?.[0] || null)}
                      className="w-full text-sm text-gray-600 font-body file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:bg-brand-blue file:text-white file:font-bold hover:file:bg-brand-blue-dark"
                    />
                    <p className="text-xs text-gray-500 font-body mt-2">
                      Its pacing, shot order, and energy are copied. Its branding and
                      product are not.
                    </p>
                  </div>

                  <button
                    onClick={handleRecreate}
                    disabled={mediaBusy !== null || !inspirationFile}
                    className="w-full mt-3 bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-4 py-2.5 rounded-xl transition-colors disabled:opacity-50"
                  >
                    {mediaBusy === 'recreate' ? 'Recreating…' : 'Recreate with our product'}
                  </button>
                </>
              )}

              {videoProgress && (
                <p className="text-sm text-gray-600 font-body mt-2">
                  {videoProgress} — video can take up to several minutes. You can leave this tab open.
                </p>
              )}
            </div>

            <div>
              <span className="block text-sm font-bold text-deep-navy mb-2 font-body">Media</span>
              <div className="grid grid-cols-4 gap-3">
                {[...generatedMedia, ...assets].map((asset) => (
                  <button
                    key={asset.file}
                    onClick={() => setSelectedAsset(asset)}
                    title={asset.description}
                    className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                      selectedAsset?.file === asset.file
                        ? 'border-brand-blue ring-2 ring-brand-blue/30'
                        : 'border-ice-teal-light hover:border-ice-teal'
                    }`}
                  >
                    {asset.kind === 'video' ? (
                      <video src={asset.url} className="w-full h-full object-cover" muted playsInline />
                    ) : (
                      <img src={asset.url} alt={asset.description} className="w-full h-full object-cover" />
                    )}
                    {asset.kind && (
                      <span className="absolute bottom-1 right-1 bg-deep-navy/80 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                        {asset.kind === 'video' ? 'REEL' : 'AI'}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleSaveDraft}
                disabled={isSaving}
                className="flex-1 bg-frost-white hover:bg-ice-teal/20 text-deep-navy font-bold px-6 py-3 rounded-xl transition-colors border border-ice-teal-light disabled:opacity-50"
              >
                {isSaving ? 'Saving…' : 'Save Draft'}
              </button>
              <button
                onClick={handleSaveAndSubmit}
                disabled={isSaving}
                className="flex-1 bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-6 py-3 rounded-xl transition-colors disabled:opacity-50"
              >
                Submit for Approval
              </button>
            </div>

            {editingPostId && (
              <button
                onClick={resetComposer}
                className="w-full text-sm text-gray-500 hover:text-deep-navy font-body"
              >
                Cancel edit
              </button>
            )}
          </div>

          {/* Preview */}
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Preview</h3>

            <div className="max-w-sm mx-auto border border-gray-200 rounded-xl overflow-hidden bg-white">
              <div className="flex items-center gap-3 p-3 border-b border-gray-100">
                <div className="w-8 h-8 rounded-full bg-ice-teal flex items-center justify-center text-sm">
                  🐧
                </div>
                <span className="font-bold text-sm text-deep-navy">waddleandsip</span>
              </div>

              {selectedAsset && selectedAsset.kind === 'video' ? (
                <video
                  src={selectedAsset.url}
                  className="w-full aspect-square object-cover bg-black"
                  controls
                  muted
                  playsInline
                />
              ) : selectedAsset ? (
                <img
                  src={selectedAsset.url}
                  alt={selectedAsset.description}
                  className="w-full aspect-square object-cover"
                />
              ) : (
                <div className="w-full aspect-square bg-frost-white flex items-center justify-center text-gray-400 font-body text-sm">
                  No media selected
                </div>
              )}

              <div className="p-3">
                <p className="text-sm text-deep-navy font-body whitespace-pre-wrap break-words">
                  {previewCaption || 'Your caption will appear here.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approvals */}
      {/* Trends — social listening feeding strategy and prompts */}
      {studioTab === 'trends' && (
        <TrendsComponent
          onUsePrompt={(prompt, mediaType) => {
            setMediaPrompt(prompt);
            setStudioTab('compose');
            notify('success', `Prompt loaded. Hit Generate ${mediaType === 'video' ? 'Video' : 'Image'}.`);
          }}
          onUseCaption={(newCaption, newHashtags, promptForMedia, mediaType) => {
            setCaption(newCaption);
            setHashtags(newHashtags);
            // Carry the shot description across too, so Compose opens ready to
            // produce the post rather than only to word it.
            if (promptForMedia) setMediaPrompt(promptForMedia);
            setStudioTab('compose');
            notify(
              'success',
              promptForMedia
                ? `Caption and ${mediaType === 'video' ? 'reel' : 'image'} prompt loaded — generate the media below.`
                : 'Caption loaded into the composer.',
            );
          }}
        />
      )}

      {studioTab === 'approve' && (
        <div className="space-y-6">
          {/* Waiting on a decision */}
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-xl font-title font-bold text-deep-navy mb-4">
              Waiting on You
              {pendingPosts.length > 0 && (
                <span className="ml-2 text-sm font-body font-normal text-gray-500">
                  {pendingPosts.length} to review
                </span>
              )}
            </h3>

            {pendingPosts.length === 0 ? (
              <p className="text-gray-600 font-body py-6 text-center">
                Nothing waiting for approval.
              </p>
            ) : (
              <div className="space-y-4">
                {pendingPosts.map((post) => (
                  <div key={post._id} className="bg-frost-white rounded-xl p-4 flex gap-4">
                    {post.mediaUrls[0] && (
                      post.mediaType === 'VIDEO' ? (
                        <video src={post.mediaUrls[0]} className="w-24 h-24 rounded-lg object-cover flex-shrink-0 bg-black" muted playsInline />
                      ) : (
                        <img src={post.mediaUrls[0]} alt="" className="w-24 h-24 rounded-lg object-cover flex-shrink-0" />
                      )
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-deep-navy mb-2 whitespace-pre-wrap">{post.caption}</p>
                      {post.hashtags.length > 0 && (
                        <p className="text-sm text-brand-blue font-body mb-3 break-words">
                          {post.hashtags.map((t) => `#${t}`).join(' ')}
                        </p>
                      )}
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => handleApprove(post._id)}
                          disabled={busyPost !== null}
                          className="bg-scarf-green hover:opacity-90 text-white font-bold px-4 py-2 rounded-lg text-sm transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {busyAction(post._id) === 'approve' ? 'Approving…' : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleReject(post._id)}
                          disabled={busyPost !== null}
                          className="bg-brand-red hover:bg-brand-red-light text-white font-bold px-4 py-2 rounded-lg text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {busyAction(post._id) === 'reject' ? 'Sending back…' : 'Send Back'}
                        </button>
                        <button
                          onClick={() => handleEdit(post)}
                          disabled={isBusy(post._id)}
                          className="bg-white hover:bg-ice-teal/20 text-deep-navy font-bold px-4 py-2 rounded-lg text-sm border border-ice-teal-light transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Drafts. Shown here rather than buried in History so unfinished
              work stays in view instead of being silently forgotten. */}
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-xl font-title font-bold text-deep-navy mb-4">
              Drafts
              {draftPosts.length > 0 && (
                <span className="ml-2 text-sm font-body font-normal text-gray-500">
                  {draftPosts.length} not yet submitted
                </span>
              )}
            </h3>

            {draftPosts.length === 0 ? (
              <p className="text-gray-600 font-body py-6 text-center">
                No drafts. Anything you save without submitting shows up here.
              </p>
            ) : (
              <div className="space-y-4">
                {draftPosts.map((post) => (
                  <div key={post._id} className="bg-frost-white rounded-xl p-4 flex gap-4">
                    {post.mediaUrls[0] && (
                      post.mediaType === 'VIDEO' ? (
                        <video src={post.mediaUrls[0]} className="w-20 h-20 rounded-lg object-cover flex-shrink-0 bg-black" muted playsInline />
                      ) : (
                        <img src={post.mediaUrls[0]} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
                      )
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-deep-navy mb-3 whitespace-pre-wrap line-clamp-3">{post.caption}</p>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => handleSubmitForApproval(post._id)}
                          disabled={busyPost !== null}
                          className="bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-4 py-2 rounded-lg text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {busyAction(post._id) === 'submit' ? 'Submitting…' : 'Submit for Approval'}
                        </button>
                        <button
                          onClick={() => handleEdit(post)}
                          disabled={isBusy(post._id)}
                          className="bg-white hover:bg-ice-teal/20 text-deep-navy font-bold px-4 py-2 rounded-lg text-sm border border-ice-teal-light transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Publish — approved work waiting to go out */}
      {studioTab === 'publish' && (
        <>
        <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
          <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Ready to Publish</h3>

          {approvedPosts.length === 0 ? (
            <p className="text-gray-600 font-body py-8 text-center">
              Nothing approved yet. Anything you approve lands here.
            </p>
          ) : (
            <div className="space-y-4">
              {approvedPosts.map((post) => (
                <div key={post._id} className="bg-frost-white rounded-xl p-4 flex gap-4">
                  {post.mediaUrls[0] && (
                    post.mediaType === 'VIDEO' ? (
                      <video
                        src={post.mediaUrls[0]}
                        className="w-24 h-24 rounded-lg object-cover flex-shrink-0 bg-black"
                        muted
                        playsInline
                      />
                    ) : (
                      <img
                        src={post.mediaUrls[0]}
                        alt=""
                        className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
                      />
                    )
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-deep-navy mb-2 whitespace-pre-wrap">{post.caption}</p>
                    {post.hashtags.length > 0 && (
                      <p className="text-sm text-brand-blue font-body mb-3 break-words">
                        {post.hashtags.map((t) => `#${t}`).join(' ')}
                      </p>
                    )}
                    <div className="flex gap-2 flex-wrap items-center">
                      <button
                        onClick={() => handlePublish(post._id)}
                        disabled={busyPost !== null}
                        className="bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-4 py-2 rounded-lg text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {busyAction(post._id) === 'publish' ? 'Publishing…' : 'Publish Now'}
                      </button>
                      <button
                        onClick={() => handleEdit(post)}
                        disabled={busyPost !== null}
                        className="bg-white hover:bg-ice-teal/20 text-deep-navy font-bold px-4 py-2 rounded-lg text-sm border border-ice-teal-light transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Edit
                      </button>
                      {busyAction(post._id) === 'publish' ? (
                        <span className="text-xs text-brand-blue font-body">
                          Waiting for Instagram to confirm. Do not close this tab.
                          {post.mediaType === 'VIDEO' && ' Video is transcoded first, so this can take a minute.'}
                        </span>
                      ) : post.mediaType === 'VIDEO' && (
                        <span className="text-xs text-gray-500 font-body">
                          Publishes as a Reel — Instagram transcodes first, so this takes a moment.
                        </span>
                      )}
                    </div>

                    {/* Schedule for later. Sits under Publish Now rather than
                        behind a modal: choosing a time is the common case for
                        planned content, and a modal would hide it. */}
                    <div className="flex gap-2 flex-wrap items-center mt-3 pt-3 border-t border-ice-teal-light">
                      <label
                        htmlFor={`schedule-${post._id}`}
                        className="text-sm font-bold text-deep-navy"
                      >
                        Schedule for
                      </label>
                      <input
                        id={`schedule-${post._id}`}
                        type="datetime-local"
                        value={scheduleDrafts[post._id] || ''}
                        min={nowInBrandZone()}
                        onChange={(e) => setScheduleDrafts((prev) => ({
                          ...prev, [post._id]: e.target.value,
                        }))}
                        disabled={busyPost !== null}
                        className="px-3 py-2 border border-ice-teal-light rounded-lg text-sm text-deep-navy focus:outline-none focus:border-brand-blue disabled:opacity-40"
                      />
                      <span className="text-xs text-gray-500 font-body">{BRAND_TZ_LABEL}</span>
                      <button
                        onClick={() => handleSchedule(post._id)}
                        disabled={busyPost !== null || !scheduleDrafts[post._id]}
                        className="bg-jasmine hover:bg-jasmine/80 text-oxford-blue font-bold px-4 py-2 rounded-lg text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {busyAction(post._id) === 'schedule' ? 'Scheduling…' : 'Schedule'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

          {/* The queue. Its own card under Ready to Publish, because "what is
              going out and when" is a different question from "what could I
              send right now". */}
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm mt-6">
            <div className="flex justify-between items-baseline mb-4">
              <h3 className="text-xl font-title font-bold text-deep-navy">Scheduled</h3>
              <p className="text-sm text-gray-500 font-body">
                Times shown in {BRAND_TZ_LABEL}
              </p>
            </div>

            {scheduledPosts.length === 0 ? (
              <p className="text-gray-600 font-body py-8 text-center">
                Nothing scheduled. Pick a date on an approved post above and it will go out on its own.
              </p>
            ) : (
              <div className="space-y-3">
                {scheduledPosts.map((post) => {
                  const due = post.scheduledFor ? new Date(post.scheduledFor) : null;
                  const overdue = Boolean(due && due.getTime() < Date.now());

                  return (
                    <div key={post._id} className="bg-frost-white rounded-xl p-4 flex gap-4 items-start">
                      {post.mediaUrls[0] && (
                        <img
                          src={post.mediaUrls[0]}
                          alt=""
                          className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-deep-navy">
                          {due
                            ? due.toLocaleString('en-US', {
                              timeZone: 'America/New_York',
                              dateStyle: 'medium',
                              timeStyle: 'short',
                            })
                            : 'No time set'}
                          {' '}
                          <span className="font-normal text-gray-500 text-sm">{BRAND_TZ_LABEL}</span>
                        </p>
                        <p className="font-body text-deep-navy/80 text-sm truncate">{post.caption}</p>

                        {/* Due but still here. Almost always means the
                            scheduler has not run — worth saying so plainly
                            rather than leaving a row that looks stuck. */}
                        {overdue && post.status === 'scheduled' && (
                          <p className="text-xs text-brand-red font-body mt-1">
                            Past its time and still queued — the scheduler may not be running.
                          </p>
                        )}
                        {post.status === 'publishing' && (
                          <p className="text-xs text-brand-blue font-body mt-1">Publishing now…</p>
                        )}
                      </div>

                      {post.status === 'scheduled' && (
                        <div className="flex gap-2 flex-wrap items-center justify-end">
                          <input
                            type="datetime-local"
                            aria-label="New time"
                            value={scheduleDrafts[post._id] || ''}
                            min={nowInBrandZone()}
                            onChange={(e) => setScheduleDrafts((prev) => ({
                              ...prev, [post._id]: e.target.value,
                            }))}
                            disabled={busyPost !== null}
                            className="px-2 py-1 border border-ice-teal-light rounded-lg text-sm text-deep-navy focus:outline-none focus:border-brand-blue disabled:opacity-40"
                          />
                          <button
                            onClick={() => handleSchedule(post._id)}
                            disabled={busyPost !== null || !scheduleDrafts[post._id]}
                            className="bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-3 py-1 rounded-lg text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {busyAction(post._id) === 'schedule' ? 'Moving…' : 'Move'}
                          </button>
                          <button
                            onClick={() => handleUnschedule(post._id)}
                            disabled={busyPost !== null}
                            className="text-brand-red hover:underline font-bold text-sm disabled:opacity-40"
                          >
                            {busyAction(post._id) === 'unschedule' ? 'Cancelling…' : 'Cancel'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* History */}
      {/* History sits below the pipeline as reference, collapsed by default so
          it does not compete with the stage you are actually working in. */}
      <div>
        <button
          onClick={() => setShowHistory((v) => !v)}
          className="text-sm font-bold text-gray-600 hover:text-deep-navy font-body flex items-center gap-2 transition-colors"
        >
          <span className={`inline-block transition-transform ${showHistory ? 'rotate-90' : ''}`}>›</span>
          History
          {allPosts.length > 0 && (
            <span className="text-gray-400 font-normal">({allPosts.length})</span>
          )}
        </button>
      </div>

      {showHistory && (
        <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
          <h3 className="text-xl font-title font-bold text-deep-navy mb-4">All Posts</h3>

          {allPosts.length === 0 ? (
            <p className="text-gray-600 font-body py-8 text-center">No posts yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-ice-teal-light">
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Caption</th>
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Status</th>
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Engagement</th>
                    <th className="text-left py-3 px-4 text-deep-navy font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allPosts.map((post) => (
                    <tr key={post._id} className="border-b border-gray-100 hover:bg-frost-white transition-colors">
                      <td className="py-3 px-4 font-body text-deep-navy max-w-xs truncate">
                        {post.caption}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${STATUS_STYLES[post.status] || 'bg-gray-100 text-gray-700'}`}>
                          {formatStatus(post.status)}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-body text-gray-600 text-sm">
                        {post.status === 'published'
                          ? `${post.engagement.likes} likes · ${post.engagement.comments} comments`
                          : '—'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          {post.status === 'approved' && (
                            <button
                              onClick={() => handlePublish(post._id)}
                              disabled={busyPost !== null}
                              className="text-scarf-green hover:opacity-70 font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              {busyAction(post._id) === 'publish' ? 'Publishing…' : 'Publish'}
                            </button>
                          )}
                          {post.status !== 'published' && (
                            <>
                              <button
                                onClick={() => handleEdit(post)}
                                disabled={busyPost !== null}
                                className="text-brand-blue hover:text-brand-blue-dark font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(post._id)}
                                disabled={busyPost !== null}
                                className="text-brand-red hover:text-brand-red-light font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                {busyAction(post._id) === 'delete' ? 'Deleting…' : 'Delete'}
                              </button>
                            </>
                          )}
                          {post.igPermalink && (
                            <a
                              href={post.igPermalink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-brand-blue hover:text-brand-blue-dark font-bold text-sm"
                            >
                              View
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContentStudioComponent;
